/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Avatar, 
  Menu, 
  MenuItem, 
  Divider, 
  Badge, 
  Button, 
  Box, 
  Tooltip,
  Typography
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAutoTranslate } from '../common/T';
import { 
  List as MenuIcon, 
  X as CloseIcon, 
  Person as PersonIcon, 
  ShieldLock as ShieldLockIcon, 
  Key as KeyIcon, 
  BoxArrowRight as BoxArrowRightIcon,
  House as HouseIcon,
  Tools,
  GraphUpArrow,
  People,
  Scooter,
  BoxSeamFill,
  FileEarmarkText,
  Map as MapIcon,
  CurrencyExchange,
  ChatDotsFill,
  ChevronRight,
  Shop,
  Speedometer2,
  Grid,
  CalendarCheck,
  Gear,
  BoxSeam,
  Folder2Open,
} from 'react-bootstrap-icons';
import { useAuth } from '../userManagement/AuthContext';
import useIsCustomerOnly from '../CustomerManagement/useIsCustomerOnly';
import actorApi from '../../api/actorApi';
import LanguageSwitcher from '../common/LanguageSwitcher';
import IncomingCallBanner from '../CommunicationTunnel/IncomingCallBanner';
import { getMissedCallsCount } from '../../api/communicationApi';
import AdminNotificationBell from './AdminNotificationBell';
import { canReceiveCalls } from '../../utils/customerPrivacy';
import PmiTopBar, { AppSwitch, ERP_LAST_PATH_KEY, PMI_LAST_PATH_KEY, readPath } from './PmiTopBar';
import CustomerBugReportAccountMenu, { BugReportAccountMenuItems } from '../common/BugReportAccountMenu';
import './Layout.css';

const isPmiNavigationItem = (item) => (
  typeof item.path === 'string' && (
    item.path.startsWith('/admin/pmi-platform') ||
    item.path === '/admin/pmi' ||
    item.path.startsWith('/admin/pmi/') ||
    item.path.startsWith('/admin/pmi-') ||
    item.path === '/admin/hospitality' ||
    item.path.startsWith('/admin/hospitality/')
  )
);

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const at = useAutoTranslate();
  // Initialize sidebar as closed on mobile, open on desktop
  const isMobileInitial = window.innerWidth <= 768;
  const [showSidebar, setShowSidebar] = useState(!isMobileInitial);
  const [isMobile, setIsMobile] = useState(isMobileInitial);
  const [openSections, setOpenSections] = useState(() => new Set());
  const [openSubCategories, setOpenSubCategories] = useState(() => new Set());
  const sidebarRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, isAdmin, hasAnyPermission, userPermissions } = useAuth();
  const [missedCallsCount, setMissedCallsCount] = useState(0);

  const isCustomerOnly = useIsCustomerOnly();

  // Incoming calls restricted to owner emails + CEO/Developer tags.
  const canHandleCalls = canReceiveCalls({ ...user, permissions: userPermissions });

  // Redirect pure customers to customer landing if they access other pages
  useEffect(() => {
    if (isAuthenticated && isCustomerOnly && !location.pathname.startsWith('/customer') && location.pathname !== '/admin/bug-reports') {
      navigate(localStorage.getItem('pmiCustomerSession') === 'true' ? '/customer/pmi' : '/customer');
    }
  }, [isAuthenticated, isCustomerOnly, location.pathname, navigate]);

  // Poll missed calls count every 2 minutes (was 30s — hammered actor-management DB pool)
  useEffect(() => {
    if (!isAuthenticated || isCustomerOnly || !canHandleCalls) {
      setMissedCallsCount(0);
      return;
    }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const data = await getMissedCallsCount();
        if (!cancelled) setMissedCallsCount(data.unread_count || 0);
      } catch (_) { /* silent */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 120000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAuthenticated, isCustomerOnly, canHandleCalls]);

  // Ref for storing sidebar state in localStorage
  useEffect(() => {
    // On desktop, always show the sidebar (ignore stored closed state)
    if (!isMobileInitial) {
      setShowSidebar(true);
      return;
    }
    // On mobile, restore stored state
    const storedSidebarState = localStorage.getItem('sidebarVisible');
    if (storedSidebarState !== null) {
      setShowSidebar(storedSidebarState === 'true');
    }
  }, [isMobileInitial]);

  // Function to get properly formatted profile picture URL
  const getFormattedProfilePictureUrl = () => {
    if (!user || !user.profile_picture_url) return null;
    
    // Use actorApi to get correct URL based on environment
    if (user.actor_id) {
      return actorApi.getProfilePictureUrl(user.actor_id);
    }
    return user.profile_picture_url;
  };

  // Function to get current page title with optional shortening for mobile
  const getCurrentPageTitle = () => {
    // Map paths to titles
    const pathTitles = {
      '/admin-dashboard': '',
      '/repair-analytics': at("Analytics & KPIs"),
      '/vehicles': at(t('pageTitles.vehicles')),
      '/vehicles/brands': at(t('pageTitles.vehicleBrands')),
      '/vehicles/brand-manager': at("Brand Manager"),
      '/vehicles/prototypes': at(t('pageTitles.vehiclePrototypes')),
      '/vehicles/attribute-definitions': at(t('pageTitles.attributeCatalogue')),
      '/vehicles/instances': at(t('pageTitles.vehicleInstances')),
      '/documents': at(t('pageTitles.documents')),
      '/documents/generate': at(t('pageTitles.generateDocument')),
      '/documents/history': at(t('pageTitles.documentHistory')),
      '/my-workspace': at(t('pageTitles.myWorkspace', 'Mein Arbeitsbereich')),
      '/actors/awards': at(t('pageTitles.awards', 'Auszeichnungen')),
      '/notifications': at(t('pageTitles.notifications')),
      '/notifications/templates': at(t('pageTitles.notificationTemplates')),
      '/notifications/events': at(t('pageTitles.notificationEvents')),
      '/notifications/logs': at(t('pageTitles.notificationLogs')),
      '/supply-management': at(t('pageTitles.supplyManagement')),
      '/supply-management/suppliers': at(t('pageTitles.suppliers')),
      '/supply-management/products': at(t('pageTitles.products')),
      '/supply-management/supply-conditions': at(t('pageTitles.supplyConditions')),
      '/supply-management/supply-offers': at(t('pageTitles.supplyOffers')),
      '/supply-management/supply-orders': at(t('pageTitles.supplyOrders')),
      '/supply-management/deliveries': at(t('pageTitles.deliveries')),
      '/supply-management/price-monitoring': at(t('pageTitles.priceCharts')),
      '/supply-management/auto-pricing': at(t('pageTitles.autoPricing')),
      '/repair-process': at(t('pageTitles.repairProcess')),
      '/repair-damage-check': at("Damage check"),
      '/repair-management': at(t('pageTitles.repairManagement')),
      '/driver/dashboard': at(t('pageTitles.driverDashboard')),
      '/driver/bookings': at(t('pageTitles.driverBookings')),
      '/driver-management': at(t('pageTitles.logisticsRoutes')),
      '/admin/tour-dashboard': at(t('pageTitles.tourDashboard', 'Tour Dashboard')),
      '/admin/routing-rules': at(t('pageTitles.routingRules', 'Auto-Routing Rules')),
      '/admin/pmi-command-center': at("PMI Command Center"),
      '/admin/pmi-api-connections': at("API Connections"),
      '/admin/pmi-platform': at("PMI Platform"),
      '/admin/pmi-platform/dashboard': at("Production Dashboard"),
      '/admin/pmi-platform/overview': at("PMI Overview"),
      '/admin/pmi-platform/tasks': at("Teamaufgaben"),
      '/admin/pmi-platform/operations': at("PMI Service Operations"),
      '/admin/pmi-platform/command-center': at("Sales & Channels"),
      '/admin/pmi-platform/channel-hub': at("Channel Hub"),
      '/admin/pmi-platform/control-center': at("PMI Control Center"),
      '/admin/pmi-platform/api-connections': at("PMI API Connections"),
      '/admin/pmi-platform/hospitality': at("MyCity Hospitality ERP"),
      '/admin/pmi-platform/restaurant': at("Restaurant Operations"),
      '/admin/pmi-platform/products': at("Products"),
      '/admin/pmi-platform/suppliers': at("PMI Suppliers"),
      '/admin/pmi-platform/orders': at("PMI Operations"),
      '/admin/pmi-platform/pricing': at("PMI Pricing"),
      '/admin/pmi-platform/widgets': at("PMI Widgets & Plans"),
      '/admin/pmi-platform/booking': at("PMI Booking"),
      '/admin/pmi': at("PMI Service Operations"),
      '/admin/hospitality': at("MyCity Hospitality ERP"),
      '/admin/pmi-restaurant': at("Restaurant Operations"),
      '/profile': at(t('pageTitles.myProfile')),
      '/permissions': at(t('pageTitles.myPermissions')),
      '/change-password': at(t('pageTitles.changePassword')),
      '/admin': at(t('pageTitles.admin')),
      '/admin/users': at(t('pageTitles.userManagement')),
      '/admin/roles': at(t('pageTitles.roleManagement')),
      '/admin/permissions': at(t('pageTitles.permissionManagement')),
    };
    
    // Check if we have a direct match
    if (pathTitles[location.pathname]) {
      return pathTitles[location.pathname];
    }
    
    // Check for partial matches
    for (const path in pathTitles) {
      if (location.pathname.startsWith(path)) {
        return pathTitles[path];
      }
    }
    
    // Default title
    return at(t('pageTitles.erpTitle'));
  };

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      
      // Update mobile state
      if (mobile !== isMobile) {
        setIsMobile(mobile);
        
        // Auto-show sidebar on desktop, hide on mobile
        if (!mobile) {
          setShowSidebar(true);
        } else {
          setShowSidebar(false);
        }
      }
    };
    
    // Initial setup
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (isMobile && showSidebar && sidebarRef.current) {
        // Check if click is outside the sidebar
        const hamburgerBtn = document.querySelector('.hamburger-btn');
        
        if (!sidebarRef.current.contains(e.target) && 
            (!hamburgerBtn || !hamburgerBtn.contains(e.target))) {
          setShowSidebar(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showSidebar, isMobile]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (isMobile) {
      setShowSidebar(false);
    }
  }, [location.pathname, isMobile]);

  // Function to handle sidebar toggle with proper state management
  const toggleSidebar = () => {
    setShowSidebar(prevState => {
      const newState = !prevState;
      // Save sidebar state to localStorage for persistence
      localStorage.setItem('sidebarVisible', newState.toString());
      return newState;
    });
  };

  // Define compact sidebar groups with hover submenus.
  const allMenuItems = useMemo(() => ([
    {
      path: '/admin/pmi-platform/overview',
      label: at("PMI Platform"),
      icon: <Grid size={16} />,
      permissions: ['manage_settings', 'view_repair_process', 'view_assigned_repair_process', 'edit_repair_process', 'work_tasks.read'],
      children: [
        { path: '/admin/pmi-platform/tasks', label: at("Teamaufgaben"), permissions: ['work_tasks.read'] },
        { path: '/admin/pmi-platform/overview', label: at("Overview"), permissions: ['manage_settings', 'view_repair_process', 'edit_repair_process'] },
        // ── PLATFORM ADMIN — your cockpit: sell & grant the 3 products ──
        {
          id: 'pmi-platform-admin',
          label: at("Platform Admin"),
          icon: <Gear size={13} />,
          isSubCategory: true,
          children: [
            { path: '/admin/pmi-platform/control-center', label: at("Control Center"),   permissions: ['manage_settings', 'view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/customers',    label: at("Customers & Access"), permissions: ['manage_settings', 'view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/widgets',      label: at("Widgets & Plans"),    permissions: ['manage_settings', 'view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/operations',   label: at("Services & Plans catalogue"), permissions: ['view_repair_process', 'view_assigned_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/api-connections', label: at("API Connections"), permissions: ['edit_repair_process', 'manage_settings'] },
          ],
        },
        // ── PRODUCT 1 · PRODUCTS — the sellable product and everything that runs it ──
        {
          id: 'pmi-product-products',
          label: at("Products"),
          icon: <BoxSeam size={13} />,
          isSubCategory: true,
          children: [
            { path: '/admin/pmi-platform/products',     label: at("Products"),           permissions: ['manage_settings', 'view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/orders',       label: at("Shop Orders"),        permissions: ['manage_settings', 'view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/widgets?widget=product_order', label: at("Product Widgets & Plans"), permissions: ['manage_settings', 'view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/widget-studio/products', label: at("Widget Studio · Products"), permissions: ['manage_settings', 'manage_pmi', 'edit_repair_process'] },
          ],
        },
        // ── PRODUCT 2 · MYCITY HOSPITALITY ──
        {
          id: 'pmi-hospitality-booking',
          label: at("MyCity Hospitality"),
          icon: <CalendarCheck size={13} />,
          isSubCategory: true,
          children: [
            { path: '/admin/pmi-platform/hospitality', label: at("Hospitality workspace"), permissions: ['view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/hospitality?view=connections', label: at("Hospitality Connections"), permissions: ['view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/widgets?widget=hospitality', label: at("Hospitality Widgets & Plans"), permissions: ['manage_settings', 'view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/widget-studio/hospitality', label: at("Widget Studio · Hospitality"), permissions: ['manage_settings', 'manage_pmi', 'edit_repair_process'] },
          ],
        },
        // ── PRODUCT 3 · RESTAURANT ──
        {
          id: 'pmi-restaurant',
          label: at("Restaurant"),
          icon: <Shop size={13} />,
          isSubCategory: true,
          children: [
            { path: '/admin/pmi-platform/restaurant', label: at("Restaurant Operations"), permissions: ['view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/restaurant?view=connections', label: at("Restaurant Connections"), permissions: ['view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/widgets?widget=restaurant_order', label: at("Restaurant Widgets & Plans"), permissions: ['manage_settings', 'view_repair_process', 'edit_repair_process'] },
            { path: '/admin/pmi-platform/widget-studio/restaurant', label: at("Widget Studio · Restaurant"), permissions: ['manage_settings', 'manage_pmi', 'edit_repair_process'] },
          ],
        },
      ],
    },

    // ── OPERATIONS & FULFILMENT — internal eScooterLab work, part of the ERP ──
    {
      id: 'erp-operations-fulfilment',
      path: '/admin/operations',
      label: at(t('salesUi.nav.operations')),
      icon: <Speedometer2 size={16} />,
      children: [
        { path: '/admin/operations',              label: at("Today"),                 permissions: ['view_dashboard', 'view_analytics', 'view_repair_process', 'view_assigned_repair_process'] },
        { path: '/admin/order-pipeline',          label: at("Orders & fulfilment"),   permissions: ['view_supply', 'edit_supply', 'manage_settings'] },
        { path: '/staff',                         label: at("Team & capacity"),       permissions: ['view_repair_process', 'edit_repair_process', 'manage_settings'] },
      ],
    },

    {
      path: '/repair-processes',
      label: at(t('nav.repairProcess')),
      icon: <Tools size={16} />,
      permissions: ['view_repair_process', 'view_assigned_repair_process', 'create_repair_process', 'edit_repair_process'],
      children: [
        { path: '/repair-processes', label: at(t('nav.repairProcess')), permissions: ['view_repair_process', 'view_assigned_repair_process', 'create_repair_process', 'edit_repair_process'] },
        { path: '/repair-calendar', label: at(t('nav.repairCalendar', 'Calendar')), permissions: ['view_repair_process', 'view_assigned_repair_process', 'view_analytics', 'view_dashboard'] },
        { path: '/repair-damage-check', label: at(t('nav.damageCheck', 'Damage check (3D)')), permissions: ['view_repair_process', 'view_assigned_repair_process', 'create_repair_process', 'edit_repair_process'] }
      ]
    },
    {
      path: '/repair-analytics',
      label: at(t('nav.analytics')),
      icon: <GraphUpArrow size={16} />,
      permissions: ['view_dashboard', 'view_analytics', 'view_actors'],
      children: [
        { path: '/repair-analytics', label: at("Analytics & KPIs"), permissions: ['view_analytics', 'view_dashboard'] },
        { path: '/admin/executive-kpis', label: at("Executive KPIs"), permissions: ['view_analytics', 'view_dashboard', 'manage_settings'] },
        { path: '/intelligence', label: at(t('nav.intelligence', 'Intelligence')), permissions: ['view_analytics', 'view_dashboard'] }
      ]
    },
    {
      path: '/vehicles',
      label: at(t('nav.vehicles')),
      icon: <Scooter size={16} />,
      permissions: ['view_vehicles', 'create_vehicles', 'edit_vehicles'],
      children: [
        { path: '/vehicles/brands', label: at(t('nav_sub.brandsModels')), permissions: ['view_vehicles', 'create_vehicles', 'edit_vehicles'] },
        { path: '/vehicles/instances', label: at(t('nav_sub.vehicleInstances')), permissions: ['view_vehicles', 'create_vehicles', 'edit_vehicles'] },
        { path: '/used-scooters', label: at("Used Scooters"), permissions: ['view_vehicles', 'create_vehicles', 'edit_vehicles'] },
        { path: '/vehicles/review-queue', label: at("Review Queue"), permissions: ['view_vehicles', 'create_vehicles', 'edit_vehicles'] },
        { path: '/vehicles/catalogue-health', label: at("Catalogue Health"), permissions: ['view_vehicles', 'create_vehicles', 'edit_vehicles'] },
        { path: '/vehicles/attribute-definitions', label: at(t('nav_sub.attributeCatalogue')), permissions: ['view_vehicles', 'create_vehicles', 'edit_vehicles'] }
      ]
    },
    {
      path: '/vehicles/marketplaces',
      label: at("Marketplaces"),
      icon: <Shop size={16} />,
      permissions: ['view_vehicles', 'create_vehicles', 'edit_vehicles', 'manage_settings', 'view_supply', 'edit_supply'],
      children: [
        { path: '/vehicles/marketplaces', label: at("Marketplaces"), permissions: ['view_vehicles', 'create_vehicles', 'edit_vehicles'] },
        { path: '/admin/dealers', label: at("Dealers · White-Label"), permissions: ['manage_settings', 'view_supply', 'edit_supply'] },
      ]
    },
    {
      path: '/supply-management',
      label: at("Products"),
      icon: <BoxSeamFill size={16} />,
      permissions: ['view_supply', 'create_supply', 'edit_supply', 'view_products', 'create_products', 'edit_products', 'delete_products', 'scrap_products', 'view_spare_parts', 'create_spare_parts', 'edit_spare_parts', 'view_repair_process', 'view_assigned_repair_process', 'edit_repair_process'],
      children: [
        { path: '/spare-parts', label: at(t('nav.spareParts')), permissions: ['view_spare_parts', 'create_spare_parts', 'edit_spare_parts'] },
        { path: '/supply-management/products', label: at(t('nav_sub.products')), permissions: ['view_products', 'create_products', 'edit_products', 'delete_products', 'scrap_products'] },
        { path: '/supply-management/document-imports', label: at("Import Documents"), permissions: ['view_supply', 'create_supply', 'edit_supply'] },
        { path: '/supply-management/suppliers', label: at(t('nav_sub.suppliers')), permissions: ['view_supply', 'create_supply', 'edit_supply'] },
        { path: '/supply-management/supply-conditions', label: at(t('nav_sub.supplyConditions')), permissions: ['view_supply', 'create_supply', 'edit_supply'] },
        { path: '/supply-management/supply-offers', label: at(t('nav_sub.supplyOffers')), permissions: ['view_supply', 'create_supply', 'edit_supply'] },
        { path: '/orders', label: at(t('nav.orders', 'Orders')), permissions: ['view_supply', 'edit_supply', 'manage_settings'] },
        { path: '/supply-management/supply-orders', label: at(t('nav_sub.supplyOrders')), permissions: ['view_supply', 'create_supply', 'edit_supply'] },
        { path: '/supply-management/deliveries', label: at(t('nav_sub.deliveries')), permissions: ['view_supply', 'create_supply', 'edit_supply'] },
        { path: '/supply-management/price-monitoring', label: at(t('nav_sub.priceCharts')), permissions: ['view_supply', 'view_products'] },
        { path: '/supply-management/auto-pricing', label: at(t('nav_sub.autoPricing')), permissions: ['view_supply', 'edit_supply'] },
        { path: '/supply-management/cw', label: at(t('nav_sub.cwIntegration')), permissions: ['view_supply', 'edit_supply', 'scrap_products'] }
      ]
    },
    {
      path: '/sales',
      label: at("Sales"),
      icon: <GraphUpArrow size={16} />,
      permissions: ['view_actors', 'view_sales'],
      children: [
        { path: '/sales/dashboard', label: at(t('salesUi.nav.dashboard')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/pipeline', label: at(t('salesUi.nav.pipeline')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/leads', label: at(t('salesUi.nav.leads')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/maps', label: at(t('salesUi.nav.maps')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/contacts', label: at(t('salesUi.nav.contacts')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/calendar', label: at(t('salesUi.nav.calendar')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/activities', label: at(t('salesUi.nav.activities')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/mail', label: at(t('salesUi.nav.mail')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/templates', label: at(t('salesUi.nav.templates')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/projects', label: at(t('salesUi.nav.projects')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/referrals', label: at(t('salesUi.nav.referrals')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/insights', label: at(t('salesUi.nav.insights')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/products', label: at(t('salesUi.nav.products')), permissions: ['view_actors', 'view_sales'] },
        // The Sales workspace keeps its own sidebar hidden inside the ERP, so
        // every destination it offered has to exist here. Automations was the
        // only one missing.
        { path: '/sales/automations', label: at(t('salesUi.nav.automations')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/settings', label: at(t('salesUi.nav.settings')), permissions: ['view_actors', 'view_sales'] },
        { path: '/sales/staff', label: at(t('salesUi.nav.staff')), permissions: ['manage_users', 'edit_users', 'create_users'] }
      ]
    },
    {
      path: '/finance',
      label: at(t('nav.finance')),
      icon: <CurrencyExchange size={16} />,
      permissions: [
        'view_finance_dashboard', 'view_finance_invoices', 'view_finance_companies',
        'view_order_invoices', 'view_order_repair_invoices', 'view_order_webshop_orders', 'view_order_finance_invoices',
        'view_finance', 'create_invoices', 'edit_finance', 'view_supply', 'edit_supply', 'manage_settings',
      ],
      children: [
        { path: '/finance/dashboard', label: at(t('nav_sub.financeDashboard')), permissions: ['view_finance_dashboard', 'view_finance', 'create_invoices', 'edit_finance'] },
        { path: '/finance/invoices', label: at("Invoices / Bills"), permissions: ['view_finance_invoices', 'view_finance', 'create_invoices', 'edit_finance'] },
        { path: '/orders/invoices', label: at("Order Invoices"), permissions: ['view_order_invoices', 'view_order_repair_invoices', 'view_order_webshop_orders', 'view_order_finance_invoices', 'view_supply', 'edit_supply', 'manage_settings'] },
        { path: '/finance/companies', label: at("Company Management"), permissions: ['view_finance_companies', 'view_finance', 'edit_finance', 'manage_settings'] }
      ]
    },
    {
      path: '/notifications',
      label: at("Notifications"),
      icon: <Badge badgeContent={0} color="error"><Tools size={16} /></Badge>, // replaced dynamically below
      permissions: ['view_notifications', 'manage_notifications'],
      children: [
        { path: '/notifications', label: at("Alerts"), permissions: ['view_notifications', 'manage_notifications'] },
        { path: '/notifications/templates', label: at("Templates"), permissions: ['view_notifications', 'manage_notifications'] },
        { path: '/notifications/events', label: at("Events"), permissions: ['view_notifications', 'manage_notifications'] },
        { path: '/notifications/logs', label: at("Logs"), permissions: ['view_notifications', 'manage_notifications'] }
      ]
    },
    {
      // Everybody's own private folder. No permissions key on purpose: a
      // personal workspace belongs to whoever is signed in, and there is no
      // right to grant or withhold. It has no children, so it stands as its
      // own category rather than hiding inside somebody else's.
      path: '/my-workspace',
      label: at(t('nav.myWorkspace', 'Mein Arbeitsbereich')),
      icon: <Folder2Open size={16} />,
    },
    {
      path: '/actors',
      label: at(t('nav.actorManagement')),
      icon: <People size={16} />,
      permissions: ['view_actors', 'create_actors', 'edit_actors', 'view_users', 'create_users', 'edit_users', 'view_tags', 'manage_tags', 'manage_settings'],
      children: [
        { path: '/actors', label: at(t('nav.actorManagement')), permissions: ['view_actors', 'create_actors', 'edit_actors'] },
        { path: '/actors/workshops', label: at(t('nav.workshops', 'Workshops')), permissions: ['view_dashboard', 'view_analytics', 'view_actors'] },
        { path: '/actors/mechanics', label: at(t('nav.mechanics', 'Mechanics')), permissions: ['view_dashboard', 'view_analytics', 'view_actors'] },
        // Recognition belongs to the Actor module and is managed for all
        // actors at once here, not on one actor's own page.
        { path: '/actors/awards', label: at(t('nav.awards', 'Auszeichnungen')), permissions: ['view_actors', 'edit_actors', 'create_actors'] },
        { path: '/customer', label: at(t('nav.customers')), permissions: ['view_actors'] },
        { path: '/admin/service-requests', label: at("Service Requests"), permissions: ['view_actors', 'manage_settings'] },
        { path: '/admin/survey-dashboard', label: at(t('nav.surveyDashboard', 'Survey Dashboard')), permissions: ['view_actors', 'manage_settings'] },
        { path: '/admin/booking-drafts', label: at("Booking Drafts"), permissions: ['view_actors', 'manage_settings'] },
        { path: '/users', label: at(t('nav.users')), permissions: ['view_users', 'create_users', 'edit_users'] },
        { path: '/interfacing', label: at(t('nav.interfacing')), permissions: ['manage_settings'] },
        { path: '/actors/tags', label: at(t('nav.tags')), permissions: ['view_tags', 'manage_tags'] }
      ]
    },
    {
      path: '/lms-academy',
      label: at(t('nav.lmsAcademy')),
      icon: <FileEarmarkText size={16} />,
      permissions: ['view_lms', 'manage_lms'],
      children: [
        { path: '/lms-academy', label: at(t('nav.lmsAcademy')), permissions: ['view_lms', 'manage_lms'] }
      ]
    },
    {
      path: '/map-management',
      label: at(t('nav.logistics')),
      icon: <MapIcon size={16} />,
      permissions: ['view_map', 'manage_routes', 'view_logistics', 'manage_logistics', 'view_logistics_tours', 'view_supply', 'view_products'],
      children: [
        { path: '/admin-dashboard', label: at(t('nav.adminDashboard')), permissions: ['view_dashboard', 'view_analytics'] },
        { path: '/map-management', label: at(t('nav.map')), permissions: ['view_map', 'manage_routes'] },
        { path: '/driver/dashboard', label: at(t('nav.driver')), permissions: ['view_logistics', 'manage_logistics'] },
        { path: '/driver-management', label: at(t('nav.logistics')), permissions: ['view_logistics_tours'] },
        { path: '/admin/tour-dashboard', label: at(t('nav.tourDashboard', 'Tours')), permissions: ['view_logistics_tours'] },
        { path: '/admin/block-management', label: at(t('nav.blockManagement', 'Block Management')), permissions: ['view_logistics_tours'] },
        { path: '/admin/routing-rules', label: at(t('nav.routingRules', 'Routing Rules')), permissions: ['view_logistics_tours'] },
        { path: '/supply-management/inventory', label: at("Inventory"), permissions: ['view_supply', 'view_products', 'create_products', 'edit_products'] }
      ]
    },
    {
      path: '/documents',
      label: at(t('nav.documents')),
      icon: <FileEarmarkText size={16} />,
      permissions: ['view_documents', 'create_documents'],
      children: [
        { path: '/documents', label: at(t('nav_sub.documentPrototypes')), permissions: ['view_documents', 'create_documents'] },
        { path: '/documents/generate', label: at(t('nav_sub.generateDocument')), permissions: ['view_documents', 'create_documents'] },
        { path: '/documents/history', label: at(t('nav_sub.documentHistory')), permissions: ['view_documents', 'create_documents'] }
      ]
    },
    { path: '/communication', label: at(t('nav.communication')), icon: <ChatDotsFill size={16} />, permissions: ['view_communication'] },
    { path: '/admin/vapi-settings', label: at(t('nav.vapi', 'VAPI Voice AI')), icon: <ChatDotsFill size={16} />, permissions: ['manage_settings', 'view_communication'] },
    {
      path: '/diagnostic',
      label: at("Diagnostic"),
      icon: <Tools size={16} />,
      permissions: ['view_dashboard', 'manage_settings', 'view_bug_reports', 'bug_reports.read', 'manage_bug_reports', 'bug_reports.manage', 'admin_access'],
      children: [
        { path: '/diagnostic', label: at("Systemdiagnose"), permissions: ['view_dashboard', 'manage_settings'] },
        { path: '/admin/bug-reports', label: at("Fehlermeldungen"), permissions: [] },
      ],
    },
  ]), [t, at]);

  // Filter menu items based on user permissions (recursively supports sub-categories)
  const canViewMenuItem = useCallback((item) => {
    // PMI Platform is an internal work area until its public release.
    if (item.path === '/admin/pmi-platform/tasks') {
      return isAdmin || hasAnyPermission(['work_tasks.read']);
    }
    if (isPmiNavigationItem(item)) {
      return user?.is_admin === true;
    }
    if (item.path === '/admin/bug-reports') {
      return Boolean(user);
    }
    if (isAdmin) return true;
    if (!user) return false;
    return item.permissions?.length ? hasAnyPermission(item.permissions) : true;
  }, [isAdmin, user, hasAnyPermission]);

  const filterMenuTree = useCallback((items) => {
    return items
      .map(item => {
        if (item.children?.length) {
          const filteredChildren = filterMenuTree(item.children);
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter(item => {
        if (item.path === '/admin/pmi-platform/tasks') return canViewMenuItem(item);
        if (isPmiNavigationItem(item)) {
          return user?.is_admin === true || (item.path === '/admin/pmi-platform/overview' && item.children?.length > 0);
        }
        if (item.children) {
          return item.children.length > 0;
        }
        return canViewMenuItem(item);
      });
  }, [canViewMenuItem, user]);

  const filteredMenu = useMemo(() => filterMenuTree(allMenuItems), [filterMenuTree, allMenuItems]);
  // The PMI platform is its own workspace: in the ERP sidebar it is one entry that opens it full width
  // (on the PMI page used last); inside PMI its sections become the PMI top bar.
  const pmiMode = isAuthenticated && location.pathname.startsWith('/admin/pmi-platform');
  const pmiGroup = useMemo(() => filteredMenu.find((item) => item.path === '/admin/pmi-platform/overview' && item.children), [filteredMenu]);
  const pmiSections = useMemo(() => (pmiGroup?.children || []).map((child) => (child.isSubCategory ? { ...child, path: child.children?.[0]?.path } : child)), [pmiGroup]);
  useEffect(() => {
    const key = pmiMode ? PMI_LAST_PATH_KEY : ERP_LAST_PATH_KEY;
    if (!isAuthenticated || location.pathname === '/login') return;
    try { window.sessionStorage.setItem(key, `${location.pathname}${location.search}`); } catch { /* storage unavailable */ }
  }, [pmiMode, isAuthenticated, location.pathname, location.search]);
  const pmiLaunchPath = user?.is_admin === true ? readPath(PMI_LAST_PATH_KEY, '/admin/pmi-platform/overview') : '/admin/pmi-platform/tasks';
  const menuItems = useMemo(() => filteredMenu.map((item) => (item === pmiGroup
    ? { path: pmiLaunchPath, label: item.label, icon: item.icon, permissions: item.permissions, pmiLauncher: true }
    : item)), [filteredMenu, pmiGroup, pmiLaunchPath]);

  const isRouteActive = (path) => {
    if (!path) return false;
    const [targetPath, targetQuery] = path.split('?');
    if (targetQuery) {
      const expected = new URLSearchParams(targetQuery);
      const current = new URLSearchParams(location.search);
      if ([...expected.entries()].some(([key, value]) => current.get(key) !== value)) return false;
    }
    if (targetPath === '/admin/pmi-platform/hospitality') return location.pathname === targetPath;
    if (targetPath === '/admin/pmi-platform/restaurant') return location.pathname === targetPath;
    if (path === '/vehicles') {
      return location.pathname === '/vehicles' || (location.pathname.startsWith('/vehicles/') && !location.pathname.startsWith('/vehicles/marketplaces'));
    }
    // The actor list stays unhighlighted on the Tags/Workshops tabs of the same section.
    if (path === '/actors') {
      return location.pathname === '/actors' || (location.pathname.startsWith('/actors/') && !/^\/actors\/(tags|workshops)(\/|$)/.test(location.pathname));
    }
    if (path === '/orders') {
      return location.pathname === '/orders' || /^\/orders\/(?!invoices(?:\/|$))[^/]+/.test(location.pathname);
    }
    return location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);
  };

  const isMenuItemActive = (item) => {
    if (item.path && isRouteActive(item.path)) return true;
    if (item.children?.length) {
      return item.children.some(child => {
        if (child.path && isRouteActive(child.path)) return true;
        if (child.children?.length) {
          return child.children.some(grandchild => grandchild.path && isRouteActive(grandchild.path));
        }
        return false;
      });
    }
    return false;
  };

  const toggleSection = useCallback((path) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const toggleSubCategory = useCallback((id) => {
    setOpenSubCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    allMenuItems.forEach(item => {
      if (!item.children?.length) return;
      const isTopActive = item.path && isRouteActive(item.path);
      const activeDirectChild = item.children.find(c => !c.children && c.path && isRouteActive(c.path));
      const activeSubCategory = item.children.find(c => c.children?.some(gc => gc.path && isRouteActive(gc.path)));

      if (isTopActive || activeDirectChild || activeSubCategory) {
        setOpenSections(prev => new Set([...prev, item.path]));
        if (activeSubCategory) {
          setOpenSubCategories(prev => new Set([...prev, activeSubCategory.id || activeSubCategory.label]));
        }
      }
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderMenuItem = (item) => {
    const isActive = isMenuItemActive(item);
    const isOpen = openSections.has(item.path);
    return (
      <div
        key={item.path}
        className={`sidebar-nav-item ${item.children?.length ? 'has-children' : ''} ${isActive ? 'active' : ''} ${isOpen && item.children?.length ? 'open' : ''}`}
      >
        <Link
          to={item.path}
          className={`nav-link ${isActive ? 'active' : ''}`}
          onClick={item.children?.length ? (e) => { e.preventDefault(); toggleSection(item.path); } : undefined}
          {...(item.path === '/communication' && missedCallsCount > 0 ? { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } } : {})}
        >
          <span className="menu-icon">
            {item.path === '/notifications' ? <BoxSeamFill size={16} /> : item.icon}
          </span>
          <span className="sidebar-link-label">{item.label}</span>
          {item.path === '/communication' && missedCallsCount > 0 && (
            <span className="badge bg-danger pill sidebar-missed-badge">
              {missedCallsCount > 99 ? '99+' : missedCallsCount}
            </span>
          )}
          {item.children?.length > 0 && <ChevronRight size={13} className="sidebar-child-arrow" />}
        </Link>

        {item.children?.length > 0 && (
          <div className="sidebar-child-list">
            {item.children.map(child => {
              // ── Sub-category Accordion Group ───────────────────
              if (child.isSubCategory || child.children?.length > 0) {
                const subId = child.id || child.label;
                const isSubOpen = openSubCategories.has(subId);
                const isSubActive = child.children?.some(gc => gc.path && isRouteActive(gc.path));

                return (
                  <div key={subId} className={`sidebar-subcategory ${isSubOpen ? 'open' : ''} ${isSubActive ? 'active' : ''}`}>
                    <button
                      type="button"
                      className={`sidebar-subcategory-header ${isSubActive ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleSubCategory(subId);
                      }}
                      title={child.label}
                    >
                      <div className="sidebar-subcategory-title">
                        {child.icon && <span className="sidebar-subcategory-icon">{child.icon}</span>}
                        <span>{child.label}</span>
                      </div>
                      <div className="sidebar-subcategory-meta">
                        <span className="sidebar-subcategory-count">{child.children?.length || 0}</span>
                        <ChevronRight size={10} className="sidebar-subcategory-arrow" />
                      </div>
                    </button>
                    {isSubOpen && (
                      <div className="sidebar-subcategory-children">
                        {child.children?.map(grandchild => (
                          <div key={grandchild.path + grandchild.label} className="sidebar-child-item">
                            <Link
                              to={grandchild.path}
                              className={`nav-link ${isRouteActive(grandchild.path) ? 'active' : ''}`}
                            >
                              {grandchild.label}
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              // ── Direct Child Item ──────────────────────────────
              return (
                <div key={child.path} className="sidebar-child-item">
                  <Link
                    to={child.path}
                    className={`nav-link ${isRouteActive(child.path) ? 'active' : ''}`}
                  >
                    {child.label}
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const UserDropdown = ({ user, onLogout }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleMenuAction = (path) => {
      handleClose();
      if (path) navigate(path);
    };

    const handleLogoutClick = () => {
      handleClose();
      onLogout();
    };

    const getInitials = () => {
      if (!user || !user.email) return 'U';
      return user.email.substring(0, 1).toUpperCase();
    };

    const isUserAdmin = user.is_admin || user.role === 'admin' || user.roles?.includes('Admin') || user.roles?.includes('admin');

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={at('Account settings')}>
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              padding: '3px',
              border: '2px solid rgba(50, 205, 50, 0.3)', // #32cd32 border
              borderRadius: '50%',
              transition: 'all 0.25s ease',
              '&:hover': {
                borderColor: '#32cd32',
                transform: 'scale(1.05)',
                boxShadow: '0 0 10px rgba(50, 205, 50, 0.25)',
              }
            }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            {user.profile_picture_url ? (
              <Avatar
                src={getFormattedProfilePictureUrl()}
                alt="Profile"
                sx={{ width: 32, height: 32 }}
              />
            ) : (
              <Avatar sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: '#32cd32', // Use theme/brand #32cd32
                color: 'white',
                fontWeight: 700,
                fontSize: '0.9rem'
              }}>
                {getInitials()}
              </Avatar>
            )}
          </IconButton>
        </Tooltip>

        {!isMobile && (
          <Box 
            onClick={handleClick}
            sx={{ 
              ml: 1.2, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-start',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <Typography sx={{ 
              fontSize: '0.8rem', 
              fontWeight: 600, 
              color: '#334155',
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.2
            }}>
              {user.email}
            </Typography>
            {isUserAdmin && (
              <Typography sx={{ 
                fontSize: '0.65rem', 
                fontWeight: 750, 
                color: '#1e7025',
                bgcolor: '#f0fff0',
                px: 0.6,
                py: 0.05,
                borderRadius: '4px',
                mt: 0.25,
                lineHeight: 1
              }}>
                Admin
              </Typography>
            )}
          </Box>
        )}

        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          disableScrollLock
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 8px 24px rgba(15,23,42,0.08))',
              mt: 1.5,
              borderRadius: '12px',
              border: '1px solid rgba(0,0,0,0.06)',
              minWidth: '220px',
              p: 0.5,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 18,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
        >
          {isMobile && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', wordBreak: 'break-all' }}>
                {user.email}
              </Typography>
              {isUserAdmin && (
                <Typography sx={{ 
                  display: 'inline-block',
                  fontSize: '0.65rem', 
                  fontWeight: 750, 
                  color: '#1e7025',
                  bgcolor: '#f0fff0',
                  px: 0.6,
                  borderRadius: '4px',
                  mt: 0.5
                }}>
                  Admin
                </Typography>
              )}
            </Box>
          )}

          <MenuItem onClick={() => handleMenuAction('/')} sx={{ py: 1, fontSize: '0.82rem', fontWeight: 650, borderRadius: '8px', gap: '8px' }}>
            <HouseIcon size={14} style={{ color: '#64748b' }} />
            {at(t('layout.dashboard'))}
          </MenuItem>
          
          <Divider sx={{ my: 0.5 }} />
          
          <MenuItem onClick={() => handleMenuAction('/profile')} sx={{ py: 1, fontSize: '0.82rem', fontWeight: 650, borderRadius: '8px', gap: '8px' }}>
            <PersonIcon size={14} style={{ color: '#64748b' }} />
            {at(t('layout.myProfile'))}
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction('/permissions')} sx={{ py: 1, fontSize: '0.82rem', fontWeight: 650, borderRadius: '8px', gap: '8px' }}>
            <ShieldLockIcon size={14} style={{ color: '#64748b' }} />
            {at(t('layout.myPermissions'))}
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction('/change-password')} sx={{ py: 1, fontSize: '0.82rem', fontWeight: 650, borderRadius: '8px', gap: '8px' }}>
            <KeyIcon size={14} style={{ color: '#64748b' }} />
            {at(t('layout.changePassword'))}
          </MenuItem>
          <BugReportAccountMenuItems onSelected={handleClose} />
          
          <Divider sx={{ my: 0.5 }} />
          
          <MenuItem onClick={handleLogoutClick} sx={{ py: 1, fontSize: '0.82rem', fontWeight: 650, borderRadius: '8px', color: '#ef4444', gap: '8px' }}>
            <BoxArrowRightIcon size={14} style={{ color: '#ef4444' }} />
            {at(t('layout.logout'))}
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  if (isAuthenticated && isCustomerOnly) {
    return (
      <div style={{ background: '#f8fafc', minHeight: '100vh', width: '100%' }}>
        {location.pathname === '/admin/bug-reports' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px', background: '#fff' }}>
            <Link to="/customer">EscooterLab</Link>
            <CustomerBugReportAccountMenu user={user} onLogout={handleLogout} />
          </div>
        )}
        {children}
      </div>
    );
  }

  if (pmiMode) {
    return (
      <div className="app-container">
        <div className="main-content full-width pmi-mode">
          <PmiTopBar sections={pmiSections} right={(
            <>
              <LanguageSwitcher />
              <AdminNotificationBell />
              <UserDropdown user={user} onLogout={handleLogout} />
            </>
          )} />
          {canHandleCalls && <IncomingCallBanner key={user.id} />}
          <div className="content m-0 p-0">
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Sidebar overlay */}
      {showSidebar && isMobile && (
        <div className={`sidebar-overlay ${showSidebar ? 'show' : ''}`} onClick={toggleSidebar}></div>
      )}

      {/* Sidebar - only shown when authenticated */}
      {isAuthenticated && (
        <div 
          ref={sidebarRef}
          className={`sidebar ${showSidebar ? 'show' : 'hide'}`}
        >
          <div className="sidebar-header">
            <div className="d-flex justify-content-between align-items-center">
              <Link to="/" className="sidebar-brand" aria-label="EscooterLab">
                <img
                  src="/E-SCOOTER-LAB-Logo.svg"
                  alt="EscooterLab"
                  className="sidebar-brand-logo"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/E-SCOOTER-LAB-Logo.png'; }}
                />
              </Link>
              <button
                className="sidebar-close-btn"
                onClick={toggleSidebar}
                aria-label={at('Close menu')}
              >
                <CloseIcon size={20} />
              </button>
            </div>
          </div>
          <div className="sidebar-nav-list">
            {menuItems.map(renderMenuItem)}
          </div>

        </div>
      )}

      {/* Main content */}
      <div className={`main-content ${!isAuthenticated ? 'full-width' : ''}`}>
        <AppBar 
          position="sticky" 
          elevation={0}
          sx={{
            backgroundColor: 'white',
            color: '#1e293b',
            borderBottom: '1px solid #e2e8f0',
            height: '65px',
            justifyContent: 'center',
            zIndex: 1050,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Hamburger button - only show when sidebar is hidden */}
              {isAuthenticated && !showSidebar && (
                <IconButton 
                  edge="start" 
                  onClick={toggleSidebar}
                  aria-label={at('Open menu')}
                  className="hamburger-btn"
                  sx={{
                    color: '#32cd32',
                    bgcolor: 'rgba(50, 205, 50, 0.06)',
                    border: '1px solid rgba(50, 205, 50, 0.2)',
                    borderRadius: '8px',
                    p: '8px',
                    '&:hover': {
                      bgcolor: 'rgba(50, 205, 50, 0.12)',
                    }
                  }}
                >
                  <MenuIcon size={20} />
                </IconButton>
              )}
              
              <Link
                to="/"
                className="navbar-brand"
                title={getCurrentPageTitle()}
                aria-label={at('Go to dashboard')}
                style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <img 
                  src="/logo.png" 
                  alt="EscooterLab Logo"
                  className="navbar-logo"
                  style={{
                    height: isMobile ? '22px' : '28px',
                    width: 'auto',
                    transition: 'transform 0.2s',
                  }}
                />
              </Link>
              {isAuthenticated && pmiGroup && <AppSwitch current="erp" />}
            </Box>
          
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 } }}>
              <LanguageSwitcher />

              {isAuthenticated && <AdminNotificationBell />}

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isAuthenticated ? (
                  <UserDropdown user={user} onLogout={handleLogout} />
                ) : (
                  <Button 
                    variant="outlined" 
                    component={Link} 
                    to="/login"
                    sx={{
                      color: '#32cd32',
                      borderColor: 'rgba(50, 205, 50, 0.5)',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      textTransform: 'none',
                      px: 2,
                      py: 0.5,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#32cd32',
                        backgroundColor: 'rgba(50, 205, 50, 0.05)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    {at(t('layout.login'))}
                  </Button>
                )}
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Global incoming call banner — visible on every page */}
        {isAuthenticated && canHandleCalls && <IncomingCallBanner key={user.id} />}

        <div className="content m-0 p-0">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
